/**
 * 头像配置：每一类部件存的是部件数组里的下标。
 * 存档/传输只需要这一个扁平的小对象，体积极小。
 *
 * 随机规则（详见 docs/头像部件系统.md）：
 *   - 默认随机 = 年轻 NPC：绝不抽到带 age 标记的部件（胡须/皱纹/白发/地中海/M字额）
 *   - 女性不抽胡须；青年男性也不抽胡须（胡须是中年起的年龄件）
 *   - 年轻随机不会抽到"光头"侧发；中老年才会出现地中海/光头
 *   - 部件 weight 权重（缺省 1）：美人尖/M字额这类存在感弱的小件降权 0.35
 *   - 双马尾后发自带两颊垂束，随机时不再叠长发侧发/过肩长垂鬓
 */
import { AGE_LINES, ACCESSORIES, BEARDS, BROWS, CLOTHES, EYES, FACES, GLASSES, HAIR_BACKS, HAIR_FRONTS, HAIR_SIDES, HAIRS, MOUTHS, NOSES, Part } from './parts';
import { EYE_COLORS, HAIR_COLORS, SKINS, Tone } from './palettes';

export type Gender = 'f' | 'm';
export type Age = 'young' | 'mid' | 'old';

export interface AvatarConfig {
  /** 性别归属：决定编辑器/开局可选部件池（f=女件池，m=男件池） */
  gender: Gender;
  face: number;
  skin: number;
  hairBack: number;
  hairSides: number;
  hairFront: number;
  /** 男发（HAIRS 下标，整头一件不拆前后侧）；gender='f' 时恒 0 */
  hair: number;
  hairColor: number;
  brow: number;
  eye: number;
  eyeColor: number;
  nose: number;
  mouth: number;
  /** 五官微调：鼻/嘴 Y 偏移，-6~+6（SVG 单位 = 200 网格像素，1 网格 = 6px），0 = 默认锚位 */
  noseDy: number;
  mouthDy: number;
  beard: number;
  ageLines: number;
  cloth: number;
  glasses: number;
  acc: number;
}

export const DEFAULT_CONFIG: AvatarConfig = {
  gender: 'f',
  face: 0,
  skin: 0,
  hairBack: 0, // 波波头（PNG）
  hairSides: 0, // 无刘海（PNG）
  hairFront: 3, // 齐刘海碎须（PNG）
  hair: 0,
  hairColor: 0,
  brow: 0,
  eye: 0,
  eyeColor: 0,
  nose: 0,
  noseDy: 0,
  mouth: 0,
  mouthDy: 0,
  beard: 0,
  ageLines: 0,
  cloth: 0,
  glasses: 0,
  acc: 0,
};

export interface RandomOptions {
  gender?: Gender;
  age?: Age;
}

/** 年龄过滤：young 排除一切带 age 的部件；mid 排除 old；old 全可 */
function ageOk(p: { age?: 'mid' | 'old' }, age: Age): boolean {
  if (!p.age) return true;
  if (age === 'young') return false;
  if (age === 'mid') return p.age === 'mid';
  return true;
}

/** 按性别/年龄从部件池抽下标（支持 weight 权重，缺省 1）；池空时兜底 0 */
function pickPart(parts: Part[], gender: Gender, age: Age, exclude: number[] = []): number {
  const pool: Array<{ i: number; w: number }> = [];
  parts.forEach((p, i) => {
    if (p.gender && p.gender !== gender) return;
    if (!ageOk(p, age)) return;
    if (exclude.includes(i)) return;
    pool.push({ i, w: p.weight ?? 1 });
  });
  if (!pool.length) return 0;
  const total = pool.reduce((s, x) => s + x.w, 0);
  let r = Math.random() * total;
  for (const x of pool) {
    r -= x.w;
    if (r <= 0) return x.i;
  }
  return pool[pool.length - 1].i;
}

function pickTone(tones: Tone[], age: Age): number {
  const pool: number[] = [];
  tones.forEach((t, i) => {
    if (!ageOk(t, age)) return;
    pool.push(i);
  });
  return pool.length ? pool[Math.floor(Math.random() * pool.length)] : 0;
}

const rand = (n: number): number => Math.floor(Math.random() * n);

/** 特殊脸型的鼻/嘴 Y 微调推荐值（下颌长度不同，五官默认锚位补偿）；缺省 = 0 */
/** 每个脸型的鼻/嘴 Y 位置默认值（下颌长度不同，五官锚位不同）；没列的脸型 = 0。
 *  随机与手动切脸型都会自动落到此基准，之后可再手调 */
export const FACE_NUDGE: Record<string, { noseDy: number; mouthDy: number }> = {
  slim: { noseDy: -1, mouthDy: -1 }, // 短脸
  round: { noseDy: -1, mouthDy: -1 }, // 圆脸
  long: { noseDy: 1, mouthDy: 1 }, // 长脸
};

/** 指定脸型的鼻/嘴默认位置（查 FACE_NUDGE，未列 = 全 0） */
export function faceNudgeOf(faceIdx: number): { noseDy: number; mouthDy: number } {
  return FACE_NUDGE[FACES[faceIdx]?.id ?? ''] ?? { noseDy: 0, mouthDy: 0 };
}

/** 部件下标按性别收敛：指向的件不属于该性别时，回退到该性别第一款专属件 */
function idxForGender(parts: Part[], idx: number, gender: Gender): number {
  const p = parts[idx];
  if (p && (!p.gender || p.gender === gender)) return idx;
  const own = parts.findIndex(q => q.gender === gender);
  return own >= 0 ? own : 0;
}

/**
 * 随机一套头像。游戏随机 NPC 一律用默认参数（年轻）；
 * 剧情/职业 NPC（老导演、经纪人等）显式传 { gender, age }。
 */
export function randomConfig(opts: RandomOptions = {}): AvatarConfig {
  const gender: Gender = opts.gender ?? 'f';
  const age: Age = opts.age ?? 'young';
  const isAdult = age !== 'young';

  // 侧发先抽；侧发=披发时后发仅 波波头/长直发/长波浪（披发垂在身后需长发托底）
  const isMale = gender === 'm';
  const hairSides = pickPart(HAIR_SIDES, gender, age);
  const looseBackExclude = HAIR_SIDES[hairSides]?.id === 'sSideLoose'
    ? HAIR_BACKS.map((p, i) => ({ p, i })).filter(({ p }) => !LOOSE_BACK_OK.includes(p.id)).map(({ i }) => i)
    : [];
  const hairBack = pickPart(HAIR_BACKS, gender, age, looseBackExclude.length ? looseBackExclude : []);

  const faceIdx = pickPart(FACES, gender, age);
  const faceNudge = faceNudgeOf(faceIdx);

  return applyFrontSideRule({
    gender,
    face: faceIdx,
    skin: rand(SKINS.length),
    // 男发整头一件（HAIRS），不拆后/侧/前三层
    hair: isMale ? pickPart(HAIRS, gender, age) : 0,
    hairBack: isMale ? 0 : hairBack,
    hairSides: isMale ? 0 : hairSides,
    hairFront: isMale ? 0 : pickPart(HAIR_FRONTS, gender, age),
    hairColor: pickTone(HAIR_COLORS, age),
    brow: pickPart(BROWS, gender, age),
    eye: pickPart(EYES, gender, age),
    nose: pickPart(NOSES, gender, age),
    noseDy: faceNudge?.noseDy ?? 0,
    mouth: pickPart(MOUTHS, gender, age),
    mouthDy: faceNudge?.mouthDy ?? 0,
    beard: gender === 'm' && isAdult ? pickPart(BEARDS, gender, age) : 0,
    ageLines: isAdult ? pickPart(AGE_LINES, gender, age) : 0,
    cloth: pickPart(CLOTHES, gender, age),
    eyeColor: rand(EYE_COLORS.length),
    // 男生随机 20% 戴方框眼镜、80% 不戴（2026-09-29 定）；女随机恒不戴
    glasses: isMale && rand(5) === 0
      ? Math.max(0, GLASSES.findIndex(p => p.id === 'mGlassesSquare'))
      : 0,
    acc: 0, // 待补 PNG
  });
}

/** 防越界：载入存档后逐项收敛到合法范围；部件归属与配置性别不符时回退到该性别专属件 */
export function sanitizeConfig(raw: Partial<AvatarConfig> | null): AvatarConfig {
  const c = { ...DEFAULT_CONFIG, ...raw };
  const clamp = (v: number, n: number) => (Number.isInteger(v) && v >= 0 && v < n ? v : 0);
  const clampNudge = (v: number) => (Number.isInteger(v) && v >= -6 && v <= 6 ? v : 0);
  const gender: Gender = c.gender === 'm' ? 'm' : 'f';
  const isMale = gender === 'm';
  return applyFrontSideRule({
    gender,
    face: idxForGender(FACES, clamp(c.face, FACES.length), gender),
    skin: clamp(c.skin, SKINS.length),
    hairBack: isMale ? 0 : idxForGender(HAIR_BACKS, clamp(c.hairBack, HAIR_BACKS.length), gender),
    hairSides: isMale ? 0 : idxForGender(HAIR_SIDES, clamp(c.hairSides, HAIR_SIDES.length), gender),
    hairFront: isMale ? 0 : idxForGender(HAIR_FRONTS, clamp(c.hairFront, HAIR_FRONTS.length), gender),
    hair: isMale ? clamp(c.hair, HAIRS.length) : 0,
    hairColor: clamp(c.hairColor, HAIR_COLORS.length),
    brow: idxForGender(BROWS, clamp(c.brow, BROWS.length), gender),
    eye: idxForGender(EYES, clamp(c.eye, EYES.length), gender),
    eyeColor: clamp(c.eyeColor, EYE_COLORS.length),
    nose: idxForGender(NOSES, clamp(c.nose, NOSES.length), gender),
    noseDy: clampNudge(c.noseDy),
    mouth: idxForGender(MOUTHS, clamp(c.mouth, MOUTHS.length), gender),
    mouthDy: clampNudge(c.mouthDy),
    beard: isMale ? clamp(c.beard, BEARDS.length) : 0,
    ageLines: isMale ? clamp(c.ageLines, AGE_LINES.length) : 0,
    cloth: idxForGender(CLOTHES, clamp(c.cloth, CLOTHES.length), gender),
    glasses: clamp(c.glasses, GLASSES.length),
    acc: isMale ? 0 : idxForGender(ACCESSORIES, clamp(c.acc, ACCESSORIES.length), gender),
  });
}

/** 披发可搭配的后发白名单（编辑器/开局页置灰与 applyFrontSideRule 共用单一事实源） */
export const LOOSE_BACK_OK = ['sBob', 'sLong', 'sLongWavy'];

/** 联动规则汇总（2026-09-21 定，sanitize/random/编辑器统一走此函数）：
 *  a) 刘海=无 → 侧发强制"无刘海"；
 *  b) 侧发=披发 → 后发仅白名单内可选，非法时回退"长直发"。 */
export function applyFrontSideRule(c: AvatarConfig): AvatarConfig {
  let out = c;
  const frontNone = HAIR_FRONTS[out.hairFront]?.id === 'none';
  const sideIsNone = HAIR_SIDES[out.hairSides]?.id === 'sSideNone';
  if (frontNone && !sideIsNone) {
    const idx = HAIR_SIDES.findIndex(p => p.id === 'sSideNone');
    out = { ...out, hairSides: idx >= 0 ? idx : out.hairSides };
  }
  const sideIsLoose = HAIR_SIDES[out.hairSides]?.id === 'sSideLoose';
  if (sideIsLoose && !LOOSE_BACK_OK.includes(HAIR_BACKS[out.hairBack]?.id ?? '')) {
    const fallback = HAIR_BACKS.findIndex(p => p.id === 'sLong');
    out = { ...out, hairBack: fallback >= 0 ? fallback : out.hairBack };
  }
  return out;
}
